import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { debounceTime, startWith, takeUntil } from "rxjs/operators";
import {
    UssdMobileMoneyOperator,
    UssdMobileMoneyPaymentService,
    UssdPaymentInstruction,
    UssdPaymentStatus,
    UssdPaymentStatusPayload,
} from "../../services/ussd-mobile-money-payment.service";

@Component({
    selector: "app-ussd-mobile-money-checkout",
    templateUrl: "./ussd-mobile-money-checkout.component.html",
    styleUrls: ["./ussd-mobile-money-checkout.component.scss"],
})
export class RayalinaUssdMobileMoneyCheckoutComponent implements OnInit, OnDestroy {
    /** Montant en Ariary (MGA), aligné sur `PricingPlan.checkoutAmountMga`. */
    @Input() amountMga = 0;
    @Input() description = "Souscription Rayalina";
    @Input() metadata: Record<string, string | number | boolean | null> = {};
    /** Emitted once when le statut passe à PAID (paiement confirmé). */
    @Output() readonly paymentConfirmed = new EventEmitter<void>();

    operators: { id: UssdMobileMoneyOperator; label: string; accent: string }[] = [
        { id: "mvola", label: "MVola", accent: "#ffd101" },
        { id: "orange_money", label: "Orange Money", accent: "#ff7900" },
        { id: "airtel_money", label: "Airtel Money", accent: "#e60000" },
    ];

    selected: UssdMobileMoneyOperator | null = null;
    readonly phoneControl = new FormControl<string | null>(null, [Validators.required]);
    readonly intlTelOptions: Record<string, unknown> = {
        initialCountry: "mg",
        onlyCountries: ["mg"],
        allowDropdown: false,
        separateDialCode: true,
        loadUtils: () => import("intl-tel-input/build/js/utils.js" as any),
    };
    loading = false;
    error: string | null = null;
    instruction: UssdPaymentInstruction | null = null;
    status: UssdPaymentStatus | null = null;
    lastStatusPayload: UssdPaymentStatusPayload | null = null;

    private pollSub: Subscription | null = null;
    private paidEmitted = false;
    private readonly destroy$ = new Subject<void>();

    constructor(private readonly payments: UssdMobileMoneyPaymentService) {}

    ngOnInit(): void {
        this.phoneControl.valueChanges
            .pipe(startWith(this.phoneControl.value ?? ""), debounceTime(450), takeUntil(this.destroy$))
            .subscribe(() => this.tryAutoStartPayment());
    }

    ngOnDestroy(): void {
        this.stopPolling();
        this.destroy$.next();
        this.destroy$.complete();
    }

    selectOperator(op: UssdMobileMoneyOperator): void {
        this.selected = op;
        this.error = null;
        this.tryAutoStartPayment();
    }

    payerPhoneDigits(): string {
        return String(this.phoneControl.value ?? "").replace(/\D/g, "");
    }

    isPayerPhoneValid(): boolean {
        return this.phoneControl.valid;
    }

    /** Lien `tel:` pour ouvrir le composeur avec le code USSD (mobile). */
    ussdTelHref(ussd: string): string {
        const compact = ussd.replace(/\s/g, "");
        return `tel:${encodeURIComponent(compact)}`;
    }

    /** Dès que l’opérateur et le numéro sont valides, crée la demande et lance le suivi automatique (sans bouton). */
    private tryAutoStartPayment(): void {
        if (this.amountMga <= 0 || !this.selected || !this.isPayerPhoneValid()) {
            return;
        }
        if (this.loading || this.instruction) {
            return;
        }
        this.createAndTrackPayment();
    }

    private createAndTrackPayment(): void {
        if (this.amountMga <= 0 || !this.selected || !this.isPayerPhoneValid()) {
            return;
        }
        this.loading = true;
        this.error = null;
        this.instruction = null;
        this.status = null;
        this.lastStatusPayload = null;
        this.paidEmitted = false;
        this.stopPolling();

        this.payments
            .createPaymentRequest({
                amountMga: Math.round(this.amountMga),
                currency: "MGA",
                operator: this.selected,
                payerPhone: this.payerPhoneDigits(),
                description: this.description,
                metadata: this.metadata,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (inst) => {
                    this.instruction = inst;
                    this.status = "PENDING";
                    this.loading = false;
                    this.beginPolling(inst.referenceId);
                },
                error: (e: unknown) => {
                    this.loading = false;
                    this.error = e instanceof Error ? e.message : "Impossible de créer la demande de paiement.";
                },
            });
    }

    copyText(value: string): void {
        void navigator.clipboard?.writeText(value);
    }

    private beginPolling(referenceId: string): void {
        this.pollSub = this.payments.pollUntilTerminal(referenceId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (s) => {
                this.lastStatusPayload = s;
                this.status = s.status;
                if (s.status === "PAID" && !this.paidEmitted) {
                    this.paidEmitted = true;
                    this.paymentConfirmed.emit();
                }
            },
            error: (e: unknown) => {
                this.error = e instanceof Error ? e.message : "Erreur lors du suivi du paiement.";
            },
        });
    }

    private stopPolling(): void {
        this.pollSub?.unsubscribe();
        this.pollSub = null;
    }
}
